'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useDebouncedCallback } from 'use-debounce'

function saveScrollPos(asPath: string) {
  sessionStorage.setItem(
    `scrollPos:${asPath}`,
    JSON.stringify({ x: window.scrollX, y: window.scrollY })
  )
}

function restoreScrollPos(asPath: string, delay?: number, cb?: () => void) {
  const json = sessionStorage.getItem(`scrollPos:${asPath}`)
  const scrollPos = json ? JSON.parse(json) : undefined

  if (scrollPos) {
    if (delay) {
      setTimeout(() => {
        window.scrollTo(scrollPos.x, scrollPos.y)
        cb?.()
      }, delay)
    } else {
      window.scrollTo(scrollPos.x, scrollPos.y)
      cb?.()
    }
  } else {
    cb?.()
  }
}

function scrollToHash(hash: string, delay?: number, cb?: () => void) {
  const id = hash.replace('#', '')
  const element = document.getElementById(id)

  if (element) {
    const scrollToElement = () => {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      cb?.()
    }

    if (delay) {
      setTimeout(scrollToElement, delay)
    } else {
      scrollToElement()
    }
  } else {
    cb?.()
  }
}

export function useScrollRestoration() {
  const pathname = usePathname()

  const debouncedSaveScroll = useDebouncedCallback((path: string) => {
    saveScrollPos(path)
  }, 300)

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return
    window.history.scrollRestoration = 'manual'

    let isRestored = false
    const hash = window.location.hash

    if (hash) {
      // If there's a hash in the URL, scroll to that element
      scrollToHash(hash, 300, () => {
        isRestored = true
      })
    } else if (pathname) {
      // Otherwise, restore the saved scroll position
      restoreScrollPos(pathname, 200, () => {
        isRestored = true
      })
    }

    const handleScroll = () => {
      // Only save scroll position if there's no hash
      if (isRestored && pathname && !window.location.hash) {
        debouncedSaveScroll(pathname)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [debouncedSaveScroll, pathname])

  // Handle hash changes (e.g., clicking anchor links on the same page)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash
      if (hash) {
        scrollToHash(hash, 0)
      }
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [])
}
